import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { Observable, of, catchError } from 'rxjs';
import { ToastService } from '@ams/ui-kit';
import { LOADING_CONTEXT } from '../interceptors/loading-context.token';

export type SearchEntityType =
  | 'member'
  | 'chapter'
  | 'user'
  | 'event'
  | 'content'
  | 'group'
  | 'resource'
  | 'pipeline'
  | 'task'
  | 'product';

export interface SearchResult {
  entityType: SearchEntityType;
  entityId: string;
  title: string;
  subtitle?: string | null;
  category?: string | null;
  rank: number;
  // Backend ts_headline output. Highlight matches wrapped in <<< >>> markers
  // (plain text, not HTML). Empty/no markers → no snippet should be shown.
  snippet?: string | null;
  resourceKind?: string | null;
  folderId?: string | null;
  groupId?: string | null;
  groupName?: string | null;
  pipelineId?: string | null;
  pipelineItemId?: string | null;
}

export interface SearchResponse {
  query: string;
  results: SearchResult[];
}

@Injectable({ providedIn: 'root' })
export class SearchService {
  private readonly http = inject(HttpClient);
  private readonly toast = inject(ToastService);
  private readonly apiUrl = '/api/search';

  search(query: string, types?: SearchEntityType[], limit = 20): Observable<SearchResponse> {
    let params = new HttpParams().set('q', query).set('limit', limit.toString());
    if (types && types.length > 0) params = params.set('types', types.join(','));

    // Skip the global loading overlay — the search component renders its own
    // inline spinner while the request is in flight.
    const context = new HttpContext().set(LOADING_CONTEXT, 'none');

    return this.http.get<SearchResponse>(this.apiUrl, { params, context }).pipe(
      catchError((err) => {
        console.error('Search failed', err);
        // Surface failure to the user — otherwise the dropdown looks identical
        // to a successful empty result and they have no idea something broke.
        this.toast.error('Search failed. Please try again.');
        return of<SearchResponse>({ query, results: [] });
      }),
    );
  }
}
